import React, { useEffect } from 'react';
import { Prospect } from '../../shared/types';
import { Sparkles, ArrowRight } from 'lucide-react';
import { Badge } from '../../components/ui/Badge';

interface HotpageDemoProps {
  prospect: Prospect;
  launchType?: 'imovel' | 'veiculo' | 'geral';
}

export const HotpageDemo: React.FC<HotpageDemoProps> = ({ prospect, launchType = 'imovel' }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
    if (prospect.theme) {
      const root = document.documentElement;
      root.style.setProperty('--ym-prospect-primary', prospect.theme.primary_color || '#00C4B4');
      root.style.setProperty('--ym-prospect-bg', prospect.theme.bg_color || '#050505');
      root.style.setProperty('--ym-prospect-surface', prospect.theme.surface_color || '#111111');
      root.style.setProperty('--ym-prospect-text', prospect.theme.text_color || '#F8FAFC');
    }
  }, [prospect]);

  const handleVIPContact = () => {
    const defaultMsg = `Olá, gostaria de receber a apresentação exclusiva e tabela de pré-lançamento com a ${prospect.trade_name}.`;
    const text = encodeURIComponent(defaultMsg);
    const phone = prospect.whatsapp || '554988416296';
    window.open(`https://wa.me/${phone}?text=${text}`, '_blank');
  };

  const isRealEstate = launchType === 'imovel';

  return (
    <div className="min-h-screen bg-[var(--ym-prospect-bg)] text-[var(--ym-prospect-text)] font-sans">
      {/* Top Banner */}
      <div className="bg-gradient-to-r from-[var(--ym-prospect-primary)] to-teal-500 text-slate-950 px-4 py-2.5 text-center text-xs font-extrabold uppercase tracking-widest flex items-center justify-center gap-2">
        <Sparkles className="w-4 h-4" />
        Condições Especiais de Pré-Lançamento Exclusivo — Vagas Limitadas
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-[var(--ym-prospect-bg)]/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <span className="font-extrabold text-xl tracking-tight text-white">{prospect.trade_name}</span>
          <button 
            onClick={handleVIPContact}
            className="px-6 py-2.5 rounded-full bg-[var(--ym-prospect-primary)] text-slate-950 font-extrabold text-xs uppercase tracking-wider hover:opacity-90 transition-opacity"
          >
            Falar com Atendimento VIP
          </button>
        </div>
      </header>

      {/* Hero Launch Section */}
      <section className="py-24 px-6 relative overflow-hidden text-center">
        <div className="max-w-4xl mx-auto relative z-10">
          <Badge variant="emerald" className="mb-4">
            {isRealEstate ? 'LANÇAMENTO IMOBILIÁRIO DE ALTO PADRÃO' : 'LANÇAMENTO EXCLUSIVO 2027'}
          </Badge>
          <h1 className="text-4xl sm:text-6xl font-black text-white tracking-tight leading-tight mb-6">
            {isRealEstate 
              ? 'Residencial Horizon — Viva no topo do conforto em São Miguel do Oeste.' 
              : 'O Novo Padrão de Sofisticação e Performance.'}
          </h1>
          <p className="text-lg text-slate-300 font-light max-w-2xl mx-auto leading-relaxed mb-10">
            Páginas de lançamento (Hotpages) são desenhadas exclusivamente para ofertas de alta margem de lucro, capturando leads qualificados antes da abertura geral do mercado.
          </p>

          <div className="p-8 rounded-3xl bg-[var(--ym-prospect-surface)] border border-white/10 shadow-2xl max-w-xl mx-auto">
            <h3 className="text-xl font-bold text-white mb-2">Garantir Tabela de Pré-Lançamento</h3>
            <p className="text-xs text-slate-400 mb-6">Cadastre-se para receber a apresentação em PDF e condições prioritárias.</p>
            
            <button 
              onClick={handleVIPContact}
              className="w-full py-4 rounded-2xl bg-[var(--ym-prospect-primary)] text-slate-950 font-black text-sm uppercase tracking-wider transition-all flex items-center justify-center gap-2 shadow-xl"
            >
              Receber Apresentação no WhatsApp <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};
